
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';

// --- Models ---
import { Driver } from '@/models/Driver.model';
import { DynamicCardState } from '@/models/App.model';
import { Load, LoadEvent, LoadStop, LoadExtra } from '@/models/Load.model';

// --- Services ---
import AppService from '@/services/app';
import CustomerService from '@/services/customer';
import DriverService from '@/services/driver';
import LoadEventService from '@/services/loadEvent';
import LoadExtraService from '@/services/loadExtra';
import LoadService from '@/services/load';
import VehicleService from '@/services/vehicle';

// --- Third Party ---
import dayjs from 'dayjs';
import Hashids from 'hashids';

@Component
export default class EditLoadPage extends Vue {
  hashids = new Hashids(process.env.VUE_APP_HASHIDS_SALT);

  clickedItem: Load = Object.assign({}, this.$store.getters['loads/emptyLoad']);

  rateString: string = '';
  finalPriceString: string = '';

  loadStops: LoadStop[] = [];
  loadEvents: LoadEvent[] = [];
  loadExtras: LoadExtra[] = [];

  valid: boolean = false;
  clicked: boolean = false;

  // used by Update Order Number Dialog
  showOrderNumberDialog: boolean = false;
  newOrderNumber: string = '';
  orderNumberValid: boolean = false;
  orderNumberClicked: boolean = false;

  // used by Add Extra Dialog
  showAddExtraDialog: boolean = false;
  addExtraDialogTitle: string = '';
  addExtraFormValid: boolean = false;
  extraTypeId: number = 0;
  extraCost: number = 0;
  newComment: string = '';
  addExtraClicked: boolean = false;

  // used by Remove Extra Dialog
  showRemoveExtraDialog: boolean = false;
  removeExtraDialogTitle: string = '';
  removeExtraFormValid: boolean = true; // can be removed if we never put the comment back in or set back to false if we do
  removeExtraClicked: boolean = false;
  removeExtraId: number = 0;

  // used by Confirm Action Dialog for Approve/Reject
  action: string = '';
  dialogContent: string = '';
  confirmActionDelay: boolean = false;
  showConfirmActionDialog: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
    requiredNumber: (value: number) => value > 0,
    requiredNumberCanBeZero: (value: number) => {
      if ((typeof value) === 'number') {
        return value.toString().length > 0 && value >= 0;
      } else {
        // will do requiredRateStringCanBeZero validation
        return true;
      }
    },
    requiredRateStringCanBeZero: (value: string) => {
      if ((typeof value) === 'string') {
        return !!value && parseFloat(value) >= 0 || '';
      } else {
        // will do requiredNumberCanBeZero validation
        return true;
      }
    },
    currency: (value: string) =>  /^\d{0,8}(?:\.\d{1,2})?$/.test(value) || 'Limited to two decimal places',
  };

  // #region Functions to display load data on page load
    offloadHeaders() {
      return [
        {
          text: 'Client',
          value: 'client',
        },
        {
          text: 'Address',
          value: 'address',
        },
        {
          text: 'Appointment Time',
          value: 'appointmentTime',
        },
        {
          text: 'Arrived',
          value: 'arrivedAt',
        },
      ];
    }

    async beforeMount() {
      const id: string = this.hashids.decode(this.$route.params.id) + '';

      if (id && id.length > 0) {
        this.clickedItem.id = parseInt(id, 10);

        let fetchLoadFromDB: boolean = true;
        let foundIndex = -1;

        // try to find this load in the the approval loads because we clicked thru from there (so we know they're already in our store)
        foundIndex = this.$store.getters['loads/approvalLoads'].findIndex( (load: Load) =>  load.id === this.clickedItem.id);

        if (foundIndex >= 0) {
          // we found it
          this.clickedItem = this.$store.getters['loads/approvalLoads'][foundIndex];
          fetchLoadFromDB = false;
          this.rateString = this.clickedItem.rate.toFixed(2);
          this.finalPriceString = this.clickedItem.finalPrice.toFixed(2);
        }

        if (fetchLoadFromDB) {
          // this didn't come from Movement or Approvals View link, or something else weird happened because we couldnt find it in the store
          await this.getLoad();
        }

        // at this point, check what status the load has ... if it is not "editable", then redirect to "View Load" instead!
        if (!(this.allowEditFunctionality() && 
          (this.clickedItem.statusId === this.$store.getters['app/statusHelper'].waitOrderNumber || 
          this.clickedItem.statusId === this.$store.getters['app/statusHelper'].waitOpprove || 
          this.clickedItem.statusId === this.$store.getters['app/statusHelper'].rejected))) {
            this.$router.push({name: 'View Load', params: {id: this.$route.params.id }});
        }

        await AppService.listStatuses(false);
        await VehicleService.listTypes(false);
        await VehicleService.listActive(true);
        await CustomerService.listAll(false);
        await DriverService.listActive(true);
        await LoadExtraService.listExtraTypes(false);
        await this.listOffloads();
        await this.listLoadEvents();
        await this.listLoadExtras();
        
      } else {
        // kick  them out, how did they even get here?!
        this.$router.push({ path: '/'});
      }
    }

    goBack() {
      this.$router.push({ name: 'Approvals' });
    }

    async getLoad() {
      try {
        const response = await LoadService.getLoad({ params: { loadId: this.clickedItem.id }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
            // kick  them out, we have no load for this Id
            this.$router.push({ path: '/'});
          } else {
            this.clickedItem = response.data;
            this.rateString = this.clickedItem.rate.toFixed(2);
            this.finalPriceString = this.clickedItem.finalPrice.toFixed(2);
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_Edit.vue - getLoad - 798 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
        
        // kick  them out, we have no load for this Id
        this.$router.push({ path: '/'});
      }
    }

    showDriverWarning(type: string, driverId: number) {
      const selectedDriver = this.$store.getters['drivers/activeDrivers'].find((item: Driver) => item.id === driverId);
      if (selectedDriver) {
        if (!selectedDriver.deletedAt) {
          switch (type) {
            case 'pdp':
              if (!selectedDriver.hasReceipt) {
                const pdpExpiryDate = dayjs(selectedDriver.pdpExpiry);

                if (dayjs().isAfter(pdpExpiryDate)) {
                  return 'overdue';
                } else if (dayjs().isAfter(pdpExpiryDate.subtract(21, 'days'))) {
                  return 'pdp-due';
                }
              } else {
                return 'has-pdp-receipt';
              }
              break;
          }
        }
      }
      return '';
    }

    async listOffloads() {
      try {
        const response = await LoadService.listOffloadsByLoad({ params: { loadId: this.clickedItem.id }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadStops = [];
          } else {
            this.loadStops = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_Edit.vue - listOffloads - 851 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async listLoadEvents() {
      try {
        // always check for all the comments in case someone else added one in the background - until we are using websockets
        const response = await LoadEventService.listByLoad({params: {loadId: this.clickedItem.id}});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadEvents = [];
          } else {
           this.loadEvents = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.loadEvents = [];
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_Edit.vue - listLoadEvents - 877 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async listLoadExtras() {
      try {
        // always check for all the comments in case someone else added one in the background - until we are using websockets
        const response = await LoadExtraService.listByLoad({params: {loadId: this.clickedItem.id}});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadExtras = [];
          } else {
           this.loadExtras = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.loadExtras = [];
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_Edit.vue - listLoadExtras - 877 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    getLoadCardState() {
      let result: DynamicCardState = {
        headerIcon: this.$store.getters['app/icons'].vehicles,
        headerBackgroundColor: 'primary',      
      };

      // Statuses in use:
      // WAIT_PROCESSING / WAIT_INVOICE_NO / WAIT_INVOICE_SEND should be grey with spinners/loaders
      // Offloaded 
      // New Load
      // Loading
      // En Route
      // Arrived
      
      // WAIT_OPPROVE
      // WAIT_POD_SCAN
      // SCANNED
      // REJECTED should be red

      if (this.$store.getters['app/statuses'].length > 0) {
        switch (this.clickedItem.statusId) {
          case 0:
            break;
            
          case this.$store.getters['app/statusHelper'].rejected:
            // User sees red icon when Load has status 11:REJECTED
            result = {
              headerIcon: this.$store.getters['app/statusById'](this.clickedItem.statusId).icon,
              headerBackgroundColor: 'red',
              tooltip: this.clickedItem.statusName,
            };
            break; 

          case this.$store.getters['app/statusHelper'].waitInvoiceNo: 
          case this.$store.getters['app/statusHelper'].waitProcessing:
          case this.$store.getters['app/statusHelper'].waitInvoiceSend:
            // User sees Loader when Load has status 25 - WAIT_INVOICE_NO / 30 - WAIT_PROCESSING / 35 - WAIT_INVOICE_SEND : Load procedure in process
            result = {
              headerBackgroundColor: 'grey',
              tooltip: this.clickedItem.statusName,
              loader: true,
            };
            break;

          default:
            result = {
              headerIcon: this.$store.getters['app/statusById'](this.clickedItem.statusId).icon,
              headerBackgroundColor: 'primary',
              tooltip: this.clickedItem.statusName,
            };
            break;
        }
      }

      return result;
    }

    allowAddExtraFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowRemoveExtraFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowUpdateOrderNumberFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 2},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowApproveFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 2},
      ]);
    }

    async doUpdate() {
      try {
        this.clicked = true;

        const response = await LoadService.updateApproval({ 
          id: this.clickedItem.id, 
          orderNumber: this.clickedItem.orderNumber!,
          customerId: this.clickedItem.customerId,
          from: this.clickedItem.from,
          to: this.clickedItem.to,
          rate: parseFloat(this.rateString),
          driverId: this.clickedItem.driverId!,
          horseVehicleId: this.clickedItem.horseVehicleId!,
          trailer1VehicleId: this.clickedItem.trailer1VehicleId!,
          trailer2VehicleId: this.clickedItem.trailer2VehicleId,
          startKm: this.clickedItem.startKm!,
          endKm: this.clickedItem.endKm!,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            await this.getLoad(); // to recalc Final Price
            AppService.successHandler(response.data.message)
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_Edit.vue - doUpdate - 1157 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.clicked = false;
      }
    } 
  // #endregion

  // #region Functions required for the Dialog to Update Order Number
    launchUpdateOrderNumberDialog() {
      this.newOrderNumber = '';
      this.showOrderNumberDialog = true;
    }

    async doUpdateOrderNumber() {
      try {
        this.orderNumberClicked = true;

        const response = await LoadService.updateOrderNumber({ 
          id: this.clickedItem.id, 
          orderNumber: this.newOrderNumber 
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.getLoad();
            await this.listLoadEvents();
            this.showOrderNumberDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_Edit.vue - doUpdateOrderNumber - 1100 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.orderNumberClicked = false;
      }
    } 
  // #endregion

  // #region Functions required for the Add Extra Dialog
    async launchAddExtraDialog() {
      this.extraTypeId = 0;
      this.extraCost = 0;
      this.newComment = '';

      this.addExtraDialogTitle = 'Add Extra for Load #' + this.clickedItem.id;

      this.$nextTick(() => {
        if (this.$refs.addExtraForm) {
          ((this.$refs.addExtraForm) as any).resetValidation();
        }
      });

      this.showAddExtraDialog = true;
    }

    async addLoadExtra() {
      try {
        this.addExtraClicked = true;

        const response = await LoadExtraService.createExtraFromApproval({ 
          id: this.clickedItem.id,
          extraTypeId: this.extraTypeId,
          amount: this.extraCost,
          comment: this.newComment,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.getLoad(); // to recalc Final Price
            await this.listLoadEvents();
            await this.listLoadExtras();
            this.showAddExtraDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_Edit.vue - addLoadExtra - 4221 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.addExtraClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Remove Extra Dialog
    async launchRemoveExtraDialog() {
      this.removeExtraId = 0;
      this.newComment = '';

      this.removeExtraDialogTitle = 'Remove Extra from Load #' + this.clickedItem.id;
      this.$nextTick(() => {
        if (this.$refs.removeExtraForm) {
          ((this.$refs.removeExtraForm) as any).resetValidation();
        }
      });

      this.showRemoveExtraDialog = true;
    }

    async removeExtra() {
      try {
        this.removeExtraClicked = true;

        const response = await LoadExtraService.delete({ 
          id: this.removeExtraId,
          loadId: this.clickedItem.id,
          comment: this.newComment,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.getLoad(); // to recalc Final Price
            await this.listLoadEvents();
            await this.listLoadExtras();
            this.showRemoveExtraDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_Edit.vue - removeExtra - 4221 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.removeExtraClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Approve Load
    launchRemoveOrderNumberDialog() {
      this.action = 'REMOVE_ORDER_NUM'; 
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: red">clear</span> the Order Number on Load #' + this.clickedItem.id 
      + '<br/>This Load will revert back to \'Requires Order Number\' status.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    launchApproveLoadDialog() {
      this.action = 'APPROVE'; 
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #71BC78">approve</span> Load #' + this.clickedItem.id + '.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'REMOVE_ORDER_NUM') {
        this.doRemoveOrderNumber();
      } else if (this.action === 'APPROVE') {
        this.doApproveLoad();
      }
    }

    async doRemoveOrderNumber() {
      try {
        this.confirmActionDelay = true;

        const response = await LoadService.removeOrderNumber({ id: this.clickedItem.id });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            // back to the Approvals listing cause we are done with this Load now
            this.goBack();
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_Edit.vue - doRemoveOrderNumber - 1222 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.confirmActionDelay = false;
      }
    }

    async doApproveLoad() {
      try {
        this.confirmActionDelay = true;

        const response = await LoadService.approve({ id: this.clickedItem.id });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            // back to the Approvals listing cause we are done with this Load now
            this.goBack();
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_Edit.vue - doApproveLoad - 1222 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion
}
